import React, { useCallback, useEffect, useRef, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, Route, Routes, useLocation } from "react-router-dom";

import { Button, Container, Divider, Grid } from "semantic-ui-react";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import Prompt from "utils/Prompt";
import util from "utils/utils";

import { SideNavLink } from "components/lib/SideNav";
import { Sticky } from "components/lib/UI";

import AccountSettings from "./Account";
import ApiSettings from "./ApiSettings";
import NotificationsSettings from "./Notifications";
import ProfileSettings from "./Profile";
import SecuritySettings from "./Security";

const UserSettings = () => {
  const user = useAppSelector((state) => state.user);
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const contextRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const location = useLocation();
  const path = location.pathname;

  const dispatch = useAppDispatch();
  const onProfileUpdated = useCallback(
    (profile) => dispatch(actions.user.updateProfile(profile)),
    [dispatch],
  );
  const onUpdateTags = useCallback(
    (tags) => dispatch(actions.user.updateTags(tags)),
    [dispatch],
  );

  const updateProfile = (label, value) => {
    setUnsaved(true);

    const validFields = [
      "github",
      "linkedin",
      "instagram",
      "twitter",
      "facebook",
    ];
    if (validFields.includes(label)) {
      if (util.validateUrl(value)) {
        const urlPath = util.getUrlPath(value);
        if (!urlPath) {
          // pass
        } else {
          // Split path, and remove any empty values, which would be a result of trailing/preceding slashes
          // Then take final value, to get the account id
          const splitPath = urlPath.split("/").filter((part) => part !== "");
          value = splitPath[splitPath.length - 1];
        }
      } else {
        // Split if issue accidentally includes the entire path. For example, they may try and do "/in/<id>" for linkedin
        const splitValue = value.split("/").filter((part) => part !== "");
        if (splitValue.length > 1) {
          value = splitValue[splitValue.length - 1];
        }
      }
    }

    onProfileUpdated({ ...(user?.profile || {}), [label]: value });
  };

  const toggleTag = (tag, checked) => {
    const { ownerTags = [] } = user;

    if (!checked) {
      api.tags.untagSingle(
        "users",
        tag._id,
        user._id,
        (newTag) => {
          const updatedTags = Object.assign([], ownerTags);
          updatedTags.splice(
            ownerTags.findIndex((ft) => ft._id === newTag._id),
            1,
          );
          onUpdateTags(updatedTags);
          dispatch(actions.user.updateTags(updatedTags));
        },
        (err) => toast.error(err.message),
      );
    } else {
      api.tags.tagSingle(
        "users",
        tag._id,
        user._id,
        (newTag) => {
          const updatedTags = Object.assign([], ownerTags);
          updatedTags.push(newTag);
          onUpdateTags(updatedTags);
        },
        (err) => toast.error(err.message),
      );
    }
  };

  const saveProfile = () => {
    setSaving(true);
    const profile = user?.profile;
    api.users.update(
      user._id,
      { profile },
      (user) => {
        toast.success(t("generic.typeUpdated", { type: "Profile" }));
        setSaving(false);
        setUnsaved(false);
        onProfileUpdated(user.profile);
      },
      (err) => {
        toast.error(err.message);
        setSaving(false);
      },
    );
  };

  return (
    <Container style={{ marginTop: 20, marginBottom: 20 }}>
      <Helmet title={"Settings"} />
      <Prompt
        when={location.pathname.match(/^\/preferences\/profile$/) && unsaved}
        message={"Your profile changes are unsaved. Really leave this page?"}
      />
      <div>
        <Button
          basic
          floated="right"
          icon="arrow left"
          content={t("users.settings.viewProfile")}
          as={Link}
          to={`/users/${user._id}`}
        />
      </div>
      <Divider clearing hidden />

      <div ref={contextRef}>
        <Grid stackable>
          <Grid.Column computer={4}>
            <Sticky top={60}>
              <h2 style={{ marginTop: 0 }}>{t("generic.settings")}</h2>
              <SideNavLink
                active={path === "/preferences"}
                to="/preferences"
                iconLeft="settings"
                content={t("users.settings.sections.account")}
              />
              <SideNavLink
                active={path === "/preferences/profile"}
                to="profile"
                iconLeft="address card outline"
                content={t("users.settings.sections.profile")}
              />
              <SideNavLink
                active={
                  path === "/preferences/security" ||
                  path === "/preferences/password"
                }
                to="security"
                iconLeft="lock"
                content={t("users.settings.sections.security")}
              />
              <SideNavLink
                active={path === "/preferences/notifications"}
                to="notifications"
                iconLeft="bell"
                content={t("users.settings.sections.notifications")}
              />
              <SideNavLink
                active={path === "/preferences/connections"}
                to="connections"
                iconLeft="plug"
                content={t("users.settings.sections.api")}
              />
              <Grid>
                <Grid.Row only="mobile">
                  <div
                    style={{
                      borderBottom: "solid grey 1px",
                      minWidth: "100%",
                      minHeight: "20px",
                      marginBottom: "5px",
                    }}
                  ></div>
                </Grid.Row>
              </Grid>
              {path === "/preferences/profile" && (
                <React.Fragment>
                  <Divider hidden />
                  {unsaved && (
                    <Button
                      style={{ marginBottom: "5px" }}
                      loading={saving}
                      fluid
                      primary
                      icon="save"
                      content={t("generic.saveChanges")}
                      onClick={saveProfile}
                    />
                  )}
                </React.Fragment>
              )}
            </Sticky>
          </Grid.Column>
          <Grid.Column computer={12}>
            <Routes>
              <Route path="/" element={<AccountSettings />} />
              <Route
                path="profile"
                element={
                  <ProfileSettings
                    saveProfile={saveProfile}
                    toggleTag={toggleTag}
                    updateProfile={updateProfile}
                    saving={saving}
                  />
                }
              />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="password" element={<SecuritySettings />} />
              <Route path="email" element={<SecuritySettings />} />
              <Route path="communication" element={<AccountSettings />} />
              <Route path="notifications" element={<NotificationsSettings />} />
              <Route path="connections" element={<ApiSettings />} />
            </Routes>
          </Grid.Column>
        </Grid>
      </div>
    </Container>
  );
};

export default UserSettings;
